import { useMemo, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { TimeEntry } from '@/services/timeTracking';
import { Project } from '@/shared/types';
import CustomDropdown, { DropdownOption } from '@/components/CustomDropdown';
import { calculateHourlyBudgetStatus, calculateMonetaryBudgetStatus, isExcludedBudgetDay, computeBudgetViolatingEntryIds } from '@/utils/budgetUtils';
import { WeekStartDay } from '@/utils/weekSettings';

// Add CSS for slide-in animation
const slideInAnimation = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-50%) translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = slideInAnimation;
  document.head.appendChild(style);
}

type GroupByPeriod = 'day' | 'week' | 'month' | 'year' | 'total';

interface ProjectHoursChartProps {
  timeEntries: TimeEntry[];
  projects: Project[];
  showBudgetWarnings?: boolean;
  budgetWarningColor?: string;
  weekStartsOn?: WeekStartDay;
}

interface ChartData {
  name: string;
  hours: number;
  color: string;
  originalHours?: number;
}

interface TimeSeriesData {
  date: string;
  [key: string]: number | string;
}

function ProjectHoursChartComponent({ timeEntries, projects, showBudgetWarnings = true, budgetWarningColor = '#EF4444', weekStartsOn = 1 }: ProjectHoursChartProps) {
  const [groupBy, setGroupBy] = useState<GroupByPeriod>('total');
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
  const [hoveredCenter, setHoveredCenter] = useState(false);

  // Create dropdown options for group by periods
  const groupByOptions: DropdownOption[] = [
    { value: 'total', label: 'Total' },
    { value: 'year', label: 'Year' },
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' },
    { value: 'day', label: 'Day' }
  ];
  
  // Calculate budget status for the dashboard badge.
  // For hourly budgets: check each individual day across all entries (same logic as reporting).
  // For monetary budgets: check cumulative spend across all entries vs total budget.
  const budgetStatus = useMemo(() => {
    const projectMap = new Map(projects.map(p => [p.id, p]));
    const outOfBudgetSet = new Set<string>();

    // ── Hourly budget: group all entries by project × day, check each day ──
    // projectId → dateKey → total seconds
    const projectDaySeconds: Record<string, Record<string, number>> = {};

    for (const entry of timeEntries) {
      if (!entry.endTime || !entry.projectId) continue;

      const project = projectMap.get(entry.projectId);
      if (!project?.budget || project.budgetType !== 'hours') continue;

      const duration = entry.duration
        ?? Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000);

      const dateKey = entry.startTime.slice(0, 10); // YYYY-MM-DD (UTC, good enough for badge)
      if (isExcludedBudgetDay(dateKey, project.budgetExcludedDays ?? [0, 6])) continue;
      if (!projectDaySeconds[entry.projectId]) projectDaySeconds[entry.projectId] = {};
      projectDaySeconds[entry.projectId][dateKey] = (projectDaySeconds[entry.projectId][dateKey] ?? 0) + duration;
    }

    // Identify violating project×day combinations (for per-day tooltip ⚠️)
    const violatingDayKeys = new Set<string>(); // "projectId|dateKey"
    const violatingDays = new Map<string, Set<string>>(); // projectId → Set<dateKey>

    for (const [projectId, dayMap] of Object.entries(projectDaySeconds)) {
      const project = projectMap.get(projectId)!;
      for (const [dateKey, seconds] of Object.entries(dayMap)) {
        const status = calculateHourlyBudgetStatus(project, seconds / 3600);
        if (status !== 'in') {
          outOfBudgetSet.add(projectId);
          violatingDayKeys.add(`${projectId}|${dateKey}`);
          if (!violatingDays.has(projectId)) violatingDays.set(projectId, new Set());
          violatingDays.get(projectId)!.add(dateKey);
        }
      }
    }

    // ── Monetary budget: track which projects are over (for pie chart coloring) ──
    const projectTotalSeconds: Record<string, number> = {};
    for (const entry of timeEntries) {
      if (!entry.endTime || !entry.projectId) continue;
      const project = projectMap.get(entry.projectId);
      if (!project?.budget || project.budgetType !== 'monetary') continue;
      const duration = entry.duration
        ?? Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000);
      projectTotalSeconds[entry.projectId] = (projectTotalSeconds[entry.projectId] ?? 0) + duration;
    }
    for (const [projectId, seconds] of Object.entries(projectTotalSeconds)) {
      const project = projectMap.get(projectId)!;
      const rate = project.hourlyRate ?? 0;
      const spent = (seconds / 3600) * rate;
      if (calculateMonetaryBudgetStatus(project, spent) === 'over') {
        outOfBudgetSet.add(projectId);
      }
    }

    const outOfBudgetProjects = Array.from(outOfBudgetSet);
    const totalProjectsWithBudget = projects.filter(p => p.budget && p.budgetType).length;

    // Count violating ENTRIES using the exact same logic as the Entries OoB view:
    // timezone-aware dates, location-specific rates, same excluded-days defaults.
    const violationCount = computeBudgetViolatingEntryIds(
      timeEntries,
      projects,
      (e) => e.endTime
        ? Math.floor((new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 1000)
        : 0
    ).size;

    return { outOfBudgetProjects, violationCount, violatingDays, totalProjectsWithBudget };
  }, [timeEntries, projects]);

  // Create completely stable chart structure that never changes with timer updates
  const chartStructure = useMemo(() => {
    // Helper function for bar chart: always use original project color
    // (per-bar violation coloring is handled via <Cell> in the bar renderer)
    const getProjectColor = (_projectId: string, defaultColor: string) => defaultColor;

    // Helper function to get project color with transparency for pie chart
    const getProjectColorWithTransparency = (projectId: string, defaultColor: string) => {
      const isOverBudget = showBudgetWarnings && budgetStatus.outOfBudgetProjects.includes(projectId);
      const baseColor = isOverBudget ? budgetWarningColor : defaultColor;
      
      // Add transparency to the color
      if (baseColor.startsWith('#')) {
        // Convert hex to rgba with transparency
        const hex = baseColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.7)`; // 70% opacity
      }
      
      return baseColor;
    };

    if (groupBy === 'total') {
      // For total view (pie chart), create stable structure with transparency
      const projectIds = [...new Set(timeEntries.map(entry => entry.projectId || 'No Project'))];
      const data: ChartData[] = projectIds.map(projectId => {
        const project = projects.find(p => p.id === projectId);
        return {
          name: project?.name || 'No Project',
          hours: 0, // Will be updated dynamically
          color: getProjectColorWithTransparency(projectId, project?.color || '#6B7280')
        };
      });
      return data;
    } else {
      // For time series views (bar charts), create stable structure without transparency
      const projectIds = [...new Set(timeEntries.map(entry => entry.projectId || 'No Project'))];
      const projectNames: Record<string, string> = {};
      const projectColors: Record<string, string> = {};
      
      projectIds.forEach(projectId => {
        const project = projects.find(p => p.id === projectId);
        projectNames[projectId] = project?.name || 'No Project';
        projectColors[projectId] = getProjectColor(projectId, project?.color || '#6B7280');
      });
      
      return { projectNames, projectColors };
    }
  }, [groupBy, timeEntries, projects, budgetStatus.outOfBudgetProjects, showBudgetWarnings, budgetWarningColor]);

  // Calculate dynamic data that updates with timer but doesn't trigger chart re-render
  const chartData = useMemo(() => {
    if (groupBy === 'total') {
      const projectHours: Record<string, number> = {};
      
      timeEntries.forEach((entry) => {
        const projectId = entry.projectId || 'No Project';
        let duration = 0;
        
        try {
          // Only use static duration from completed entries, ignore running entries
          if (entry.endTime) {
            // Use duration if available, otherwise calculate it
            if (entry.duration) {
              duration = entry.duration;
            } else {
              // Calculate duration from start and end times
              const start = new Date(entry.startTime).getTime();
              const end = new Date(entry.endTime).getTime();
              duration = Math.floor((end - start) / 1000);
            }
          } else {
            // Skip running entries entirely
            return;
          }
          if (isNaN(duration) || duration < 0) {
            duration = 0;
          }
        } catch (error) {
          console.warn('Error calculating duration for entry:', entry.id, error);
          duration = 0;
        }
        
        if (!projectHours[projectId]) {
          projectHours[projectId] = 0;
        }
        projectHours[projectId] += duration;
      });

      // Update the stable structure with current data
      return (chartStructure as ChartData[]).map(item => {
        // Find the correct project ID by matching the project name
        let projectId = 'No Project';
        if (item.name !== 'No Project') {
          const project = projects.find(p => p.name === item.name);
          projectId = project?.id || 'No Project';
        }
        
        const seconds = projectHours[projectId] || 0;
        const hours = seconds / 3600;
        
        return {
          ...item,
          hours: Number(hours.toFixed(2))
        };
      }).sort((a, b) => b.hours - a.hours);
    } else {
      // For time series views
      const timeSeriesData: Record<string, Record<string, number>> = {};
      
      timeEntries.forEach(entry => {
        const projectId = entry.projectId || 'No Project';
        
        // Only use static duration from completed entries, ignore running entries
        let duration: number;
        if (entry.endTime) {
          // Use duration if available, otherwise calculate it
          if (entry.duration) {
            duration = entry.duration;
          } else {
            // Calculate duration from start and end times
            const start = new Date(entry.startTime).getTime();
            const end = new Date(entry.endTime).getTime();
            duration = Math.floor((end - start) / 1000);
          }
        } else {
          // Skip running entries entirely
          return;
        }
        
        const entryDate = new Date(entry.startTime);
        
        let dateKey: string;
        switch (groupBy) {
          case 'day':
            dateKey = entryDate.toISOString().split('T')[0];
            break;
          case 'week': {
            const weekStart = new Date(entryDate);
            const dayOfWeek = entryDate.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
            const daysFromWeekStart = weekStartsOn === 1
              ? (dayOfWeek === 0 ? 6 : dayOfWeek - 1) // Monday start: Sun→6, others subtract 1
              : dayOfWeek;                              // Sunday start: use as-is
            weekStart.setDate(entryDate.getDate() - daysFromWeekStart);
            dateKey = weekStart.toISOString().split('T')[0];
            break;
          }
          case 'month':
            dateKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}`;
            break;
          case 'year':
            dateKey = entryDate.getFullYear().toString();
            break;
          default:
            dateKey = entryDate.toISOString().split('T')[0];
        }
        
        if (!timeSeriesData[dateKey]) {
          timeSeriesData[dateKey] = {};
        }
        if (!timeSeriesData[dateKey][projectId]) {
          timeSeriesData[dateKey][projectId] = 0;
        }
        timeSeriesData[dateKey][projectId] += duration;
      });

      // Convert to array format for recharts
      const data = Object.entries(timeSeriesData).map(([date, projectHours]) => {
        const entry: TimeSeriesData = { date };
        Object.entries(projectHours).forEach(([projectId, seconds]) => {
          entry[projectId] = Number((seconds / 3600).toFixed(2));
        });
        return entry;
      }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Compute per-bar budget violations for <Cell> coloring
      // Key: period dateKey → Set of projectIds that violate in that period
      const barViolations = new Map<string, Set<string>>();
      if (showBudgetWarnings) {
        const projectMap = new Map(projects.map(p => [p.id, p]));

        // Helper: map a YYYY-MM-DD day string to the period key used on the x-axis
        const toPeriodKey = (dayKey: string): string => {
          const d = new Date(dayKey + 'T12:00:00'); // Use noon to avoid UTC day-boundary shift when computing week start
          switch (groupBy) {
            case 'day': return dayKey;
            case 'week': {
              const ws = new Date(d);
              const dow = d.getDay();
              const daysBack = weekStartsOn === 1
                ? (dow === 0 ? 6 : dow - 1)
                : dow;
              ws.setDate(d.getDate() - daysBack);
              return ws.toISOString().split('T')[0];
            }
            case 'month': return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            case 'year': return d.getFullYear().toString();
            default: return dayKey;
          }
        };

        // Collect per-day seconds per project (for period-level aggregation)
        const perDaySec: Record<string, Record<string, number>> = {};
        timeEntries.forEach(entry => {
          if (!entry.endTime || !entry.projectId) return;
          const project = projectMap.get(entry.projectId);
          if (!project?.budget) return;
          const dur = entry.duration ??
            Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000);
          const dayKey = entry.startTime.slice(0, 10);
          if (project.budgetType === 'hours' && isExcludedBudgetDay(dayKey, project.budgetExcludedDays ?? [0, 6])) return;
          if (!perDaySec[entry.projectId]) perDaySec[entry.projectId] = {};
          perDaySec[entry.projectId][dayKey] = (perDaySec[entry.projectId][dayKey] ?? 0) + dur;
        });

        for (const [projectId, dayData] of Object.entries(perDaySec)) {
          const project = projectMap.get(projectId)!;

          if (project.budgetType === 'hours') {
            if (groupBy === 'year') {
              // For year: mark the year bar if any day in it violated
              const violatingDaysForProject = budgetStatus.violatingDays.get(projectId);
              if (violatingDaysForProject) {
                for (const dayKey of violatingDaysForProject) {
                  const pKey = toPeriodKey(dayKey);
                  if (!barViolations.has(pKey)) barViolations.set(pKey, new Set());
                  barViolations.get(pKey)!.add(projectId);
                }
              }
            } else {
              // Aggregate per period, then check period-level budget
              const periodSec: Record<string, number> = {};
              for (const [dayKey, sec] of Object.entries(dayData)) {
                const pKey = toPeriodKey(dayKey);
                periodSec[pKey] = (periodSec[pKey] ?? 0) + sec;
              }
              const gran = groupBy === 'week' ? 'weekly' as const
                : groupBy === 'month' ? 'monthly' as const
                : 'daily' as const;
              for (const [pKey, sec] of Object.entries(periodSec)) {
                if (calculateHourlyBudgetStatus(project, sec / 3600, gran) !== 'in') {
                  if (!barViolations.has(pKey)) barViolations.set(pKey, new Set());
                  barViolations.get(pKey)!.add(projectId);
                }
              }
            }
          } else if (project.budgetType === 'monetary') {
            // Chronological cumulative spend: mark each period once the budget is exceeded
            const rate = project.hourlyRate ?? 0;
            let cumSpend = 0;
            for (const [dayKey, sec] of Object.entries(dayData).sort(([a], [b]) => a.localeCompare(b))) {
              cumSpend += (sec / 3600) * rate;
              if (cumSpend > (project.budget ?? Infinity)) {
                const pKey = toPeriodKey(dayKey);
                if (!barViolations.has(pKey)) barViolations.set(pKey, new Set());
                barViolations.get(pKey)!.add(projectId);
              }
            }
          }
        }
      }

      return {
        data,
        projectNames: (chartStructure as { projectNames: Record<string, string> }).projectNames,
        projectColors: (chartStructure as { projectColors: Record<string, string> }).projectColors,
        barViolations,
      };
    }
  }, [chartStructure, projects, groupBy, timeEntries, showBudgetWarnings, budgetStatus.violatingDays, weekStartsOn]);

  const totalHours = useMemo(() => {
    if (groupBy === 'total') {
      return (chartData as ChartData[]).reduce((sum, item) => sum + item.hours, 0);
    } else {
      const timeSeriesData = (chartData as { data: TimeSeriesData[] }).data;
      return timeSeriesData.reduce((sum, entry) => {
        return sum + Object.entries(entry).reduce((entrySum, [key, value]) => {
          return entrySum + (key === 'date' ? 0 : (value as number));
        }, 0);
      }, 0);
    }
  }, [chartData, groupBy]);

  // Calculate total completed entries count (excluding running entries)
  const totalEntries = useMemo(() => {
    return timeEntries.filter(entry => entry.endTime).length;
  }, [timeEntries]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ payload: unknown; name: string; value: number }>; label?: string }) => {
    if (active && payload && payload.length) {
      // Sort payload by value (hours) in descending order
      const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
      const maxItems = 4;
      const itemsToShow = sortedPayload.slice(0, maxItems);
      const hasMore = sortedPayload.length > maxItems;
      
      return (
        <div style={{ 
          position: 'absolute', 
          zIndex: 9999, 
          pointerEvents: 'none',
          animation: 'fadeIn 0.15s ease-out'
        }}>
          <div 
            className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg max-w-xs" 
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
              pointerEvents: 'none', 
              backdropFilter: 'blur(8px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
              transform: 'translateY(-50%) translateX(20px)',
              marginTop: '10px'
            }}
          >
            <p className="font-medium text-gray-900 mb-2 text-sm">{label}</p>
            <div className="space-y-1">
              {itemsToShow.map((entry, index) => {
                // Find project by name to check budget status and get actual color
                const project = projects.find(p => p.name === entry.name);
                // For day view, only flag the ⚠️ on the specific day that violated the budget.
                // For other views (week/month/year/total), fall back to project-level flag.
                const isOverBudget = project && showBudgetWarnings && (
                  groupBy === 'day'
                    ? budgetStatus.violatingDays.get(project.id)?.has(label ?? '')
                    : budgetStatus.outOfBudgetProjects.includes(project.id)
                );
                const actualColor = project?.color || '#3B82F6';
                
                return (
                  <div key={index} className="flex items-center justify-between py-1">
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: actualColor }}
                      />
                      <p className="text-sm text-gray-600 truncate" title={entry.name}>
                        {entry.name}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <span className="text-sm font-medium text-gray-900">
                        {entry.value.toFixed(1)}h
                      </span>
                      {isOverBudget && (
                        <span className="text-xs font-medium" style={{ color: budgetWarningColor }}>⚠️</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {hasMore && (
                <div className="text-xs text-gray-500 text-center pt-1 border-t border-gray-100">
                  +{sortedPayload.length - maxItems} more projects
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if ((groupBy === 'total' && (chartData as ChartData[]).length === 0) || 
      (groupBy !== 'total' && (chartData as { data: TimeSeriesData[] }).data.length === 0)) {
          return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-medium text-gray-900">Project Hours</h3>
          </div>
          <div className="text-center py-6">
            <p className="text-sm text-gray-400">No time entries found</p>
          </div>
        </div>
      );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-purple-100 rounded-lg">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Distribution</h3>
        </div>
        <div className="flex items-center space-x-3">
                      <CustomDropdown
              id="group-by-dropdown"
              options={groupByOptions}
              selectedValues={[groupBy]}
              onSelectionChange={(values) => setGroupBy(values[0] as GroupByPeriod)}
              minWidth="min-w-[120px]"
            />
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-700 font-medium">{totalHours.toFixed(1)}h</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-700 font-medium">Entries: {totalEntries || 0}</span>

            {showBudgetWarnings && budgetStatus.violationCount > 0 && (
              <>
                <span className="text-gray-400">•</span>
                <Link
                  to="/entries?groupBy=outOfBudget"
                  className="inline-flex items-center gap-1 font-medium hover:underline"
                  style={{ color: budgetWarningColor }}
                  title={`${budgetStatus.violationCount} out of budget`}
                >
                  {budgetStatus.violationCount}
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          {groupBy === 'total' ? (
            <PieChart margin={{ top: 30, right: 30, left: 30, bottom: 30 }}>
              {/* Artistic background elements */}
              <defs>
                <radialGradient id="artisticGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(139, 92, 246, 0.08)" />
                  <stop offset="100%" stopColor="rgba(139, 92, 246, 0.02)" />
                </radialGradient>
                <filter id="softGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <filter id="elegantShadow">
                  <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.08)"/>
                </filter>
                <filter id="strong3D">
                  <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="rgba(0,0,0,0.15)"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.1)"/>
                </filter>
                <filter id="innerGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <filter id="hover3D">
                  <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="rgba(0,0,0,0.2)"/>
                  <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.15)"/>
                </filter>
                <linearGradient id="centerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                  <stop offset="100%" stopColor="rgba(248,250,252,0.9)" />
                </linearGradient>
                <linearGradient id="centerHoverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,1)" />
                  <stop offset="100%" stopColor="rgba(240,245,250,0.95)" />
                </linearGradient>
              </defs>
              
              {/* Subtle background */}
              <circle cx="50%" cy="50%" r="160" fill="url(#artisticGradient)" />
              
              {/* Web-inspired decorative elements */}
              {Array.from({ length: 6 }).map((_, i) => {
                const angle = (i * 60) * (Math.PI / 180);
                const x = 50 + Math.cos(angle) * 190;
                const y = 50 + Math.sin(angle) * 190;
                return (
                  <g key={i}>
                    <circle
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="2"
                      fill="rgba(139, 92, 246, 0.3)"
                      style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))' }}
                    />
                    <line
                      x1={`${x}%`}
                      y1={`${y}%`}
                      x2={`${50 + Math.cos(angle) * 170}%`}
                      y2={`${50 + Math.sin(angle) * 170}%`}
                      stroke="rgba(139, 92, 246, 0.1)"
                      strokeWidth="1"
                      style={{ filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.05))' }}
                    />
                  </g>
                );
              })}
              
              <Pie
                data={chartData as ChartData[]}
                cx="50%"
                cy="50%"
                outerRadius={160}
                innerRadius={80}
                dataKey="hours"
                nameKey="name"
                label={false}
                filter="url(#softGlow)"
                stroke="white"
                strokeWidth={2}
                startAngle={90}
                endAngle={-270}
                style={{ cursor: 'pointer' }}
              >
                {(chartData as ChartData[]).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color} 
                    stroke="white" 
                    strokeWidth={2}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: hoveredSegment === entry.name 
                        ? 'url(#hover3D)' 
                        : 'drop-shadow(0px 3px 6px rgba(0,0,0,0.15))',
                      transform: hoveredSegment === entry.name ? 'scale(1.01)' : 'scale(1)',
                    }}
                    onMouseEnter={() => setHoveredSegment(entry.name)}
                    onMouseLeave={() => setHoveredSegment(null)}
                  />
                ))}
              </Pie>
              
              {/* Static center design without hover effects */}
              <circle 
                cx="50%" 
                cy="50%" 
                r="75" 
                fill="url(#centerGradient)"
                filter="url(#strong3D)"
                style={{ 
                  pointerEvents: 'none',
                }}
              />
              <circle 
                cx="50%" 
                cy="50%" 
                r="70" 
                fill="none" 
                stroke="rgba(139, 92, 246, 0.3)"
                strokeWidth="1.5"
                style={{ 
                  pointerEvents: 'none', 
                  filter: 'url(#innerGlow)',
                }}
              />
              
              {/* Perfectly centered text with enhanced styling */}
              <text 
                x="50%" 
                y="40%" 
                textAnchor="middle" 
                fill={hoveredCenter ? "#7C3AED" : "#8B5CF6"}
                fontSize="10" 
                fontWeight="600" 
                opacity="0.8"
                style={{ 
                  cursor: 'pointer', 
                  pointerEvents: 'auto',
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={() => setHoveredCenter(true)}
                onMouseLeave={() => setHoveredCenter(false)}
              >
                TOTAL
              </text>
              <text 
                x="50%" 
                y="52%" 
                textAnchor="middle" 
                fill={hoveredCenter ? "#111827" : "#1F2937"}
                fontSize="26" 
                fontWeight="700"
                style={{ 
                  cursor: 'pointer', 
                  pointerEvents: 'auto',
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.15))',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={() => setHoveredCenter(true)}
                onMouseLeave={() => setHoveredCenter(false)}
              >
                {totalHours.toFixed(1)}
              </text>
              <text 
                x="50%" 
                y="62%" 
                textAnchor="middle" 
                fill={hoveredCenter ? "#4B5563" : "#6B7280"}
                fontSize="9" 
                fontWeight="500"
                style={{ 
                  cursor: 'pointer', 
                  pointerEvents: 'auto',
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={() => setHoveredCenter(true)}
                onMouseLeave={() => setHoveredCenter(false)}
              >
                hours
              </text>
              
              <Tooltip 
                content={<CustomTooltip />}
                cursor={false}
                wrapperStyle={{ 
                  pointerEvents: 'none'
                }}
                offset={10}
                isAnimationActive={false}
              />
            </PieChart>
          ) : (
            // Bar chart for time series views (including year) - UNCHANGED
            <BarChart data={(chartData as { data: TimeSeriesData[] }).data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f8f9fa" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={11}
                tickFormatter={(value) => {
                  if (groupBy === 'day') {
                    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  } else if (groupBy === 'week') {
                    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  } else if (groupBy === 'month') {
                    return new Date(value + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                  } else {
                    return value;
                  }
                }}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={11}
                tickFormatter={(value) => `${value}h`}
                domain={[0, 'dataMax + dataMax * 0.1']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
              {Object.keys((chartData as { projectNames: Record<string, string> }).projectNames).map((projectId) => {
                const projectColors = (chartData as { projectColors: Record<string, string> }).projectColors;
                const barViolations = (chartData as { barViolations: Map<string, Set<string>> }).barViolations;
                const barData = (chartData as { data: TimeSeriesData[] }).data;
                const baseColor = projectColors[projectId];
                return (
                  <Bar
                    key={projectId}
                    dataKey={projectId}
                    name={(chartData as { projectNames: Record<string, string> }).projectNames[projectId]}
                    fill={baseColor}
                    radius={[2, 2, 0, 0]}
                  >
                    {barData.map((entry) => {
                      const isViolating = showBudgetWarnings &&
                        barViolations.get(entry.date as string)?.has(projectId);
                      return (
                        <Cell
                          key={`cell-${entry.date}`}
                          fill={isViolating ? budgetWarningColor : baseColor}
                        />
                      );
                    })}
                  </Bar>
                );
              })}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      

    </div>
  );
} 

export default memo(ProjectHoursChartComponent, (prevProps, nextProps) => {
  // Only re-render if timeEntries, projects, showBudgetWarnings, budgetWarningColor, or weekStartsOn actually change
  // This prevents re-renders when the timer is running but data hasn't changed
  if (prevProps.showBudgetWarnings !== nextProps.showBudgetWarnings) return false;
  if (prevProps.budgetWarningColor !== nextProps.budgetWarningColor) return false;
  if (prevProps.weekStartsOn !== nextProps.weekStartsOn) return false;

  const timeEntriesEqual = prevProps.timeEntries.length === nextProps.timeEntries.length &&
    prevProps.timeEntries.every((entry, index) => {
      const nextEntry = nextProps.timeEntries[index];
      return entry.id === nextEntry.id &&
             entry.startTime === nextEntry.startTime &&
             entry.endTime === nextEntry.endTime &&
             entry.duration === nextEntry.duration;
    });

  const projectsEqual = prevProps.projects.length === nextProps.projects.length &&
    prevProps.projects.every((project, index) => {
      const nextProject = nextProps.projects[index];
      return project.id === nextProject.id &&
             project.name === nextProject.name &&
             project.color === nextProject.color &&
             project.status === nextProject.status;
    });

  return timeEntriesEqual && projectsEqual;
}); 